<template>
    <div>
        <websocket-watcher
            :channel="wsChannel"
            @message="handleAuctionUpdate"
            @connected="setConnectedToRealtimeService"
        />
        <div class="block lg:flex">
            <page-headline>
                {{$t('pages.controlpanel.index.title')}}
            </page-headline>
            <auction-visibility
                class="pt-5 pb-5 lg:pb-3"
                :auction="auction"
                v-if="this.isRunning(this.auction)"/>
        </div>
        <div v-if="auction !== null">
            <div class="text-xl mt-3">{{auction.name}}</div>
            <stats :show-headline="false" :show-grid="true" :grid-cols="4">
                <stat :name="$t('pages.controlpanel.index.controls.auction_date')"
                      :value="auction.date_start | formatDay"
                      :same-width="true"/>
                <stat :name="$t('pages.controlpanel.index.controls.auction_time')"
                      :value="currentCalculatedAuctionTime | formatTime($t('locale.formatTime'), tz.auctionTimeZone())"
                      :second-value="currentCalculatedAuctionTime | formatTimeAmPm(tz.auctionTimeZone())"
                      :same-width="true"/>
                <stat :name="$t('pages.controlpanel.index.controls.last_activity')"
                      :value="getLastActivity | formatTime($t('locale.formatTime'), tz.auctionTimeZone())"
                      :second-value="getLastActivity | formatTimeAmPm(tz.auctionTimeZone())"
                      :same-width="true"/>
                <stat :name="$t('pages.controlpanel.index.controls.product_id')"
                      :value="auction.article_id"
                      :value-link="linkToProduct"
                      :same-width="true"/>
            </stats>
            <div
                class=" mt-8 text-center whitespace-nowrap text-3xl"
                v-if="!this.isRunning(this.auction)">
                {{$t('pages.controlpanel.index.auction_ended')}}
            </div>

            <div class="w-full block xl:flex xl:flex-row mt-8 space-x-0 xl:space-x-4">
                <div class="w-full xl:w-1/2">
                    <div class="bg-white tile h-full">
                        <emoji-stats
                            :stats="emojiStats"
                            :label="`User Emojis`"
                        ></emoji-stats>
                    </div>
                </div>
                <div class="w-full xl:w-1/2 mt-8 xl:mt-0">
                    <div class="bg-white tile h-full">
                        <producer-control
                            :auction="auction"
                            :next-live-show="nextLiveShow"
                            :next-price-drop="nextPriceDrop"
                            :current-live-show="currentLiveShow"
                            :time-next-live-show="timeNextLiveShow"
                            :time-live-show-ends="timeLiveShowEnds"
                            :time-next-price-drop="timeNextPriceDrop"
                        />
                    </div>
                </div>
            </div>

            <div class="w-full block sm:flex md:block lg:flex xl:flex-row mt-8 space-x-0 xl:space-x-4">
                <div class="w-full sm:w-1/2 md:w-full lg:w-1/2">
                    <div class="bg-white tile h-full">
                        <users-on-tta-page
                            :user-count="currentUserStats"
                            :data="usersOnTtaTrend"
                            :label="$t('pages.controlpanel.index.controls.visitors_on_tta_page')"/>
                    </div>
                </div>
                <div class="w-full sm:w-1/2 md:w-full lg:w-1/2 mt-8 sm:mt-0 md:mt-8 lg:mt-0">
                    <div class="bg-white tile h-full">
                        <total-fs-today
                            :floorSalesValue="performanceDetails?.floorsales_bid"
                            :salesValue="performanceDetails?.floorsales_sold"
                            :salesData="floorSalesTrend"
                            :label_1="$t('pages.controlpanel.index.controls.total_fs_today_1' + this.$page.props.tenant.labels_key)"
                            :label_2="$t('pages.controlpanel.index.controls.total_fs_today_2' + this.$page.props.tenant.labels_key)"/>
                    </div>
                </div>
            </div>

            <div class="w-full block sm:flex md:block xl:flex xl:flex-row mt-8 space-x-0 xl:space-x-4">
                <div class="w-full sm:w-1/2 md:w-full xl:w-1/2">
                    <div class="bg-white tile h-full flex flex-col">
                        <average-price
                            :shop-price="auction?.attributes?.shop_price"
                            :highest="performanceDetails?.max_price"
                            :average="performanceDetails?.average_price_bids"
                            :lowest="performanceDetails?.min_price"
                            :label-shop-price="$t('pages.controlpanel.index.controls.shop_price' + $page.props.tenant.labels_key)"
                            :label-highest="$t('pages.controlpanel.index.controls.highest')"
                            :label-average="$t('pages.controlpanel.index.controls.average')"
                            :label-lowest="$t('pages.controlpanel.index.controls.lowest')"
                        ></average-price>
                        <div class="flex flex-grow-1 h-full text-right pr-5 pb-5 invisible lg:visible" v-if="this.isRunning(this.auction)">
                            <button type="button" class="btn btn-primary ml-auto mt-auto" @click="adjustMaxPrice">
                                {{ $t('pages.controlpanel.index.controls.adjust_max_price') }}
                            </button>
                        </div>
                    </div>
                </div>
                <div class="w-full sm:w-1/2 md:w-full xl:w-1/2 mt-8 sm:mt-0 md:mt-8 xl:mt-0">
                    <div class="bg-white tile h-full">
                        <quantity-sold
                            :sold="performanceDetails?.quantity_sold"
                            :pending="performanceDetails?.quantity_pending"
                            :available="availableQuantity"
                            :data-sold="soldQuantityTrend">
                        </quantity-sold>
                        <div class="flex text-right pr-5 pb-5 invisible lg:visible" v-if="this.isRunning(this.auction)">
                            <button type="button" class="btn btn-primary ml-auto" @click="adjustQuantity" >
                                {{ $t('pages.controlpanel.index.controls.adjust_quantity') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mt-10 w-full">
                <tabs ref="tabs" class="w-full">
                    <tab :title="$t('pages.controlpanel.index.levels.tabs.actual')" class="px-5">
                        <auction-performance-details-overview
                            class="pb-5"
                            :performance-details="performanceDetails"
                            :auction="auction"
                            :show-quantity="true"
                        />
                        <pyramid-levels
                            class="pb-5 lg:pb-0"
                            :is-running="this.isRunning(this.auction)"
                            :levels="levels"
                            :auction="auction"
                            :time-between-price-drops-and-shows="timeBetweenPriceDropsAndShows"
                            @adjustPriceDrop="adjustPriceDropForLevel"
                            @auction-modified="auctionModified"
                            @addPriceDropPhaseBeforeLevel="addPriceDropPhaseBeforeLevel"
                        />
                    </tab>
                    <tab :title="$t('pages.controlpanel.index.levels.tabs.planned')" class="px-5">
                        <pyramid-levels-overview
                            class="pb-5"
                            :quantity="levelsPerformancePlanned.quantity"
                            :availableQuantity="levelsPerformancePlanned.quantity"
                            :maxPrice="levelsPerformancePlanned.maxPrice"
                            :minPrice="levelsPerformancePlanned.minPrice"
                            :levels="levelsPerformancePlanned.levels"
                            :shop-price="auction.attributes?.shop_price"
                            :hideAvailableQuantity="true"
                            :quantityLabel="$t('shared.pyramid_auction.levels_overview.quantity_original')"
                        />
                        <pyramid-levels-planning
                            :levels="levelsPlanned"
                        />
                    </tab>
                </tabs>
            </div>

            <adjust-max-price-modal
                ref="adjustPriceModal"
                :auction="auction"
                :current-max-price="levelsPerformance.maxPrice"
                :min-price="levelsPerformance.minPrice"
                :active-level="activeLevel"
                @hide="onAdjustMaxPricePreview()"
            />
            <adjust-quantity-modal
                ref="adjustQuantityModal"
                :auction="auction"
                :current-quantity="levelsPerformance.quantity"
                :min-quantity="calculateMinQuantityForQuantityAdaption"
                :sold-quantity="performanceDetails?.quantity_sold"
                :active-level="activeLevel"
                :pending-quantity="performanceDetails?.quantity_pending"
                @hide="onAdjustQuantityPreview()"
            />

            <adjust-price-drop-phase-modal
                ref="adjustPriceDropModal"
                :auction="auction"
                @accept="reloadData(['auction', 'originLevels', 'rawLevelsData', 'timeNextLiveShow', 'nextPriceDrop', 'nextLiveShow'])"
                :level-phase="getCurrentLevelPhaseIndex()"
                :adjusted-level="adjustingLevel"
                :current-number-of-levels="currentNumberOfLevels"
                :min-number-of-levels="getMinNumberOfLevels()"
                :current-price="currentLevelPrice"
                :current-quantity="currentQuantity"
                :min-quantity="getMinQuantityForAdjustedLevel()"
                :max-quantity="getMaxQuantityForAdjustedLevel()"
                :current-factor="currentFactor"
                :min-factor="getMinFactorForAdjustedLevel()"
                :max-factor="getMaxFactorForAdjustedLevel()"
                :min-time="minTime"
                :max-time="maxTime"
                :price-drop-time="priceDropTime"
                :show-enabled="showIsEnabled"
                :show-start="showStartTime"
                :show-end="showEndTime"
                :offset-in-seconds="timeBetweenPriceDropsAndShows"
                :live-show-time-settings="liveShowTimeSettings"
                :price-drop-time-settings="priceDropTimeSettings"
                :title="$t('shared.modals.adjust_price_drop_modal.title')"
            />
        </div>
        <div class="bg-white tile my-5" v-if="!auction">
            <div class="px-4 py-5 sm:p-6">
                <div class="flex sm:items-start sm:justify-between">
                    <div class="flex">
                        <h3 class="text-xl leading-6 font-medium mr-5"><icon name="emoji-sad" class="h-10"/></h3>
                        <div class="mt-2 max-w-xl text-sm text-gray-500">
                            <p>{{$t('pages.controlpanel.index.no_running_auction')}}</p>
                        </div>
                    </div>
                    <div class="mt-5 sm:mt-0 sm:ml-6 sm:flex-shrink-0 sm:flex sm:items-center">
                        <button @click="goToPlanning()" type="button" class="btn btn-primary">
                            {{$t('pages.controlpanel.index.go_to_planning_button_label')}}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Layout from '@/Shared/Layout';
import Icon from '@/Shared/Icon';
import PageHeadline from "@/Shared/PageHeadline";
import Status from '@/Shared/Status';
import AuctionState from "@/mixins/AuctionState";
import { Link } from '@inertiajs/vue2';
import {ErrorTypes} from "../../mixins/ErrorTypes";
import AlertMessage from "@/Shared/Alerts/AlertMessage";
import ValueBox from "@/Shared/ValueBox";
import UsersOnTtaPage from "@/Shared/Monitoring/UsersOnTtaPage";
import TotalFsToday from "@/Shared/Monitoring/TotalFsToday";
import AveragePrice from "@/Shared/Monitoring/AveragePrice";
import QuantitySold from "@/Shared/Monitoring/QuantitySold";
import ProducerControl from "@/Shared/Monitoring/ProducerControl";
import EmojiStats from "@/Shared/Monitoring/EmojiStats";
import PyramidLevels from "@/Shared/Tables/PyramidLevels";
import PyramidLevelsOverview from "@/Shared/Overviews/PyramidLevelsOverview";
import Tab from '@/Shared/Tab';
import Tabs from '@/Shared/Tabs';
import PyramidLevelsPlanning from "@/Shared/Tables/PyramidLevelsPlanning";
import AdjustMaxPriceModal from "@/Shared/Modals/AdjustMaxPriceModal";
import AdjustQuantityModal from "@/Shared/Modals/AdjustQuantityModal";
import AdjustPriceDropPhaseModal from "../../Shared/Modals/AdjustPriceDropPhaseModal";
import WebsocketWatcher from "@/Shared/WebsocketWatcher";
import Stats from "../../Shared/Stats";
import Stat from "../../Shared/Stat";
import AuctionVisibility from "../../Shared/Auction/AuctionVisibility";
import TimeZoneService from "../../Services/TimeZoneService";
import AuctionPerformanceDetailsOverview from '@/Shared/Analytics/AuctionPerformanceDetailsOverview';
import moment from "moment";

export default {
    metaInfo() {
        return {
            title: this.$t('pages.auctions.index.auctions')
        }
    },
    mixins: [
        AuctionState,
        ErrorTypes
    ],
    layout: Layout,
    components: {
        AdjustPriceDropPhaseModal,
        AdjustMaxPriceModal,
        AdjustQuantityModal,
        PyramidLevelsPlanning,
        PyramidLevelsOverview,
        ProducerControl,
        Tab,
        Tabs,
        PyramidLevels,
        QuantitySold,
        AveragePrice,
        TotalFsToday,
        UsersOnTtaPage,
        ValueBox,
        Icon,
        PageHeadline,
        Status,
        Link,
        AlertMessage,
        WebsocketWatcher,
        Stats,
        Stat,
        EmojiStats,
        AuctionVisibility,
        AuctionPerformanceDetailsOverview
    },
    props: {
        auction: Object,
        activeUserStats: Array,
        currentUserStats: Number,
        trendStats: Array,
        originLevels: Array,
        /**
         * Websocket Channel to listen to
         */
        wsChannel: String,
        lastActivity: Number,
        currentLiveShow: Object,
        nextLiveShow: Object,
        nextPriceDrop: Object,
        timeNextLiveShow: String,
        timeNextPriceDrop: String,
        timeLiveShowEnds: String,
        emojiStats: Array,
        performanceDetails: Object,
        auctionTime: Number,
        productDetailsLink: String,
        activeLevel: {
            type: Object,
            default: null
        },
        rawLevelsData: {
            type: Array,
            default: []
        },
        timeBetweenPriceDropsAndShows: {
            type: Number,
            default: 120
        },
        priceDropTimeSettings: Array,
        liveShowTimeSettings: Array
    },
    data() {
        let minTime = this.auction ? moment.tz(this.auction.date_start, this.tz?.auctionTimeZone()) : null;
        let maxTime = this.auction ? moment.tz(this.auction.date_end, this.tz?.auctionTimeZone()) : null;

        return {
            currentNumberOfLevels: 1,
            currentLevelPrice: 0,
            currentQuantity: 0,
            currentFactor: '0',
            /**
             * Connection to ably established? Being set by event from websocketWatcher component
             */
            connected: false,
            emojiReloadInterval: null,
            auctionTimeInterval: null,
            currentAuctionTime: this.auctionTime,
            reloadInProgress: false,
            minTime: minTime,
            maxTime: maxTime,
            priceDropTime: minTime,
            showIsEnabled: false,
            showStartTime: minTime,
            showEndTime: minTime,
            adjustingLevel: null,
        };
    },
    computed: {
        tz() {
            return TimeZoneService;
        },
        linkToProduct() {
            return this.productDetailsLink + this.auction.article_id;
        },
        levels() {
            return this.auction.levels.map(level => ({
                ...level,
                fixed_time: level.date_start,
                sold: level.quantity_used,
                floor_sales: level.quantity_used * level.price,
                merch_sales: this.performanceDetails?.merchSales.find(s => s.sequence == level.sequence)?.merch_sales || 0
            }));
        },
        levelsPlanned() {
            return this.originLevels === null ? this.levels : this.originLevels;
        },
        levelsPerformance() {
            return {
                quantity: this.auction.article_quantity,
                maxPrice: this.performanceDetails?.max_price,
                minPrice: this.performanceDetails?.min_price,
                levels: this.auction.levels.length
            };
        },
        levelsPerformancePlanned() {
            return {
                quantity: this.performanceDetails?.quantity_planned,
                minPrice: this.performanceDetails?.planned_min_price,
                maxPrice: this.performanceDetails?.planned_max_price,
                levels: this.performanceDetails?.planned_level_count
            }
        },
        nextLevel() {
            return this.levels.find(s => s.state === 'next');
        },
        availableQuantity() {
            return this.performanceDetails?.quantity_available;
        },
        usersOnTtaTrend() {
            let trend = [];
            for(let i = 0; i <= 23; i++) {
                let value = this.activeUserStats.find((stat) => stat.hour == i)?.count || 0;
                trend.push(value);
            }
            return trend;
        },
        soldQuantityTrend() {
            return this.createTrend('quantity');
        },
        floorSalesTrend() {
            return [
                this.createTrend('sales'),
                this.createTrend('demand')
            ];
        },
        calculateMinQuantity() {
            return this.availableQuantity;
        },
        calculateMinQuantityForQuantityAdaption() {
            return this.performanceDetails?.quantity_sold +
                this.performanceDetails?.quantity_pending +
                this.activeLevel?.quantity || 0 +
                (this.nextLevel?.price_drop == 0 ? this.nextLevel?.quantity : 0);
        },
        currentCalculatedAuctionTime() {
            return new Date(this.currentAuctionTime).toUTCString();
        },
        getLastActivity() {
            return new Date(this.lastActivity).toUTCString();
        }
    },
    methods: {
        createTrend(property) {
            let trend = [];
            for(let i = 0; i <= 23; i++) {
                let match = this.trendStats.filter((stat) => stat.label == i);
                trend.push((match.length === 0) ? 0 : match.reduce((acc, stat) => acc + parseFloat(stat[property]), 0));
            }
            return trend;
        },
        adjustMaxPrice() {
            if (this.isRunning(this.auction)) {
                this.$refs.adjustPriceModal.show();
            }
        },
        adjustQuantity() {
            if (this.isRunning(this.auction)) {
                this.$refs.adjustQuantityModal.show();
            }
        },

        adjustPriceDropForLevel(level) {
            if (this.isRunning(this.auction)) {
                this.adjustingLevel = level;
                this.currentNumberOfLevels = this.getCurrentNumberOfLevels(this.adjustingLevel);
                this.currentLevelPrice = this.getCurrentLevelPrice(this.adjustingLevel);
                this.currentQuantity = this.getCurrentLevelQuantity(this.adjustingLevel);
                this.currentFactor = this.getCurrentLevelFactor(this.adjustingLevel);
                this.minTime = this.getMinTimeForAdjustedLevel(this.adjustingLevel);
                this.maxTime = this.getMaxTimeForAdjustedLevel(this.adjustingLevel);
                this.priceDropTime = this.getPriceDropTime(this.adjustingLevel);
                this.showIsEnabled = this.isThereALiveShow(this.adjustingLevel);
                this.showStartTime = this.getShowStartTime(this.adjustingLevel);
                this.showEndTime = this.getShowEndTime(this.adjustingLevel);
                this.$nextTick(() => this.$refs.adjustPriceDropModal.show());
            }
        },
        addPriceDropPhaseBeforeLevel(level) {
            let newLevel = {};
            let time = null;

            if (!level) { // last level
                let lastLevel = this.auction.levels[this.auction.levels.length-1];
                level = {};
                level.sequence = lastLevel.sequence + 1;
                level.phase = lastLevel.phase + 1;
                level.price = lastLevel.price;
                level.quantity = 2;
                level.quantity_origin = lastLevel.quantity_origin;

                let lastPdLvl = this.auction.levels.find((lvl) => lvl.phase == lastLevel.phase && lvl.price_drop === 1);
                let minTime = moment.tz(lastPdLvl.date_start, this.tz.auctionTimeZone());
                let dates = [minTime];
                let campaign = this.getCampaignByTime(minTime);
                if (campaign && campaign.date_start) {
                    dates.push(moment.tz(campaign.date_end, this.tz.auctionTimeZone()));
                    dates.push(moment.tz(campaign.date_ended, this.tz.auctionTimeZone()));
                    minTime = moment.max(dates.filter(d => d._isValid));
                }
                let maxTime = moment.tz(this.auction.date_end, this.tz.auctionTimeZone());
                let diff = maxTime.diff(minTime, 'second') / 2;
                time = moment(minTime).add(diff, 'seconds');
            } else {
                // Add a new level and ad it to the modal
                // do a time in between the next level and the previous price drop level or the
                let minTime = this.getMinTimeForAdjustedLevel(level);
                let lvlIndex = this.auction.levels.findIndex(lvl => lvl.id === level.id) - 1;
                let prevLevel = this.auction.levels[lvlIndex < 0 ? 0 : lvlIndex ];
                let maxTime = this.getMaxTimeForAdjustedLevel(prevLevel);
                let diff = maxTime.diff(minTime, 'second') / 2;
                time = moment(minTime).add(diff, 'seconds');
            }

            newLevel = {
                auction_id: this.auction.id,
                sequence: level.sequence,
                phase: level.phase,
                state: 'future',
                price: level.price,
                quantity_origin: level.quantity_origin,
                quantity: level.quantity,
                price_drop: 1,
                date_start: time,
                id: -1
            };
            this.adjustPriceDropForLevel(newLevel);
        },
        getCurrentLevelPrice(level) {
            if (level) {
                return level.price;
            }
            return this.performanceDetails?.min_price;
        },
        getCurrentNumberOfLevels(level) {
            // need to calculate the number of levels for the currently selected level
            let retVal = this.getMinNumberOfLevels();
            if (level) {
                return this.auction.levels.filter((lvl) => lvl.phase === level.phase).length || retVal
            }
            return retVal;
        },
        getMinNumberOfLevels() {
            return 2;
        },
        getCurrentLevelQuantity(level) {
            if (level) {
                return level.quantity;
            }
            return this.getMinQuantityForAdjustedLevel();
        },
        getCurrentLevelPhaseIndex() {
            if (this.adjustingLevel) {
                return this.adjustingLevel.phase;
            }
            return 0;
        },
        getMinQuantityForAdjustedLevel() {
            return 1;
        },
        getMaxQuantityForAdjustedLevel() {
            return this.calculateMinQuantity;
        },
        getCurrentLevelFactor(level) {
            if (level) {
                let rawLevel = this.rawLevelsData.find((rawLevel) => rawLevel.level_sequence === level.phase);
                return rawLevel ? rawLevel.quantity_increase_factor : '1.0';
            }
            return this.getMinQuantityForAdjustedLevel();
        },
        getMinFactorForAdjustedLevel() {
            return '1.00';
        },
        getMaxFactorForAdjustedLevel() {
            return '10.00'; // TODO: To be refined,
        },
        goToPlanning() {
            this.$inertia.visit(route('auctions.planning'));
        },
        handleAuctionUpdate(e) {
            if (!e.name.match(/^Auction/) && e.data.auction.id !== this.auction.id) {
                return;
            }
            if (this.connected) {
               this.reloadData( [
                   'auction',
                   'auctionTime',
                   'activeUserStats',
                   'currentUserStats',
                   'trendStats',
                   'lastActivity',
                   'currentLiveShow',
                   'nextLiveShow',
                   'timeNextLiveShow',
                   'timeLiveShowEnds',
                   'activeLevel',
                   'performanceDetails'
               ]);
            }
        },
        setConnectedToRealtimeService(status) {
            this.connected = status;
        },
        reloadData(itemsToLoad) {
            const self = this;
            if (self.reloadInProgress === true) {
                return;
            }
            this.$inertia.reload({
                preserveScroll: true,
                only: itemsToLoad,
                onStart: visit => {
                    self.reloadInProgress = true;
                },
                onFinish: visit => {
                    self.reloadInProgress = false;
                    self.currentAuctionTime = self.auctionTime
                    self.clearTimers();
                    self.startTimers();
                }
            });
        },
        startTimers() {
            const self = this;
            this.emojiReloadInterval = setInterval(function() {
                if (self.isRunning(self.auction) || (!self.isRunning(self.auction) && self.currentLiveShow)) {
                    self.reloadData([
                        'emojiStats',
                        'auctionTime',
                        'activeUserStats',
                        'currentUserStats',
                        'trendStats',
                        'nextPriceDrop',
                        'lastActivity',
                        'currentLiveShow',
                        'nextLiveShow',
                        'timeNextLiveShow',
                        'timeLiveShowEnds',
                        'performanceDetails'
                    ]);
                }
            }, 10000);
            this.auctionTimeInterval = setInterval(function() {
                self.currentAuctionTime += 1000;
            }, 1000);
        },
        clearTimers() {
            clearInterval(this.emojiReloadInterval);
            clearInterval(this.auctionTimeInterval);
        },
        getMinTimeForAdjustedLevel(level) {
            if (!level || level.sequence == 0) {
                return moment.tz(this.auction.date_start, this.tz.auctionTimeZone());
            }
            let previousPriceDropTime = this.getPreviousPriceDropTime(level);
            let dates = [previousPriceDropTime];
            let campaign = this.getCampaignByTime(previousPriceDropTime);
            if (campaign && campaign.date_start) {
                dates.push(moment.tz(campaign.date_end, this.tz.auctionTimeZone()));
                dates.push(moment.tz(campaign.date_ended, this.tz.auctionTimeZone()));
                return moment.max(dates.filter(d => d._isValid));
            }
            return previousPriceDropTime;
        },
        getMaxTimeForAdjustedLevel(level) {
            if (!level || level.sequence ==  level.length - 1) {
                return moment.tz(this.auction.date_end, this.tz.auctionTimeZone());
            }

            let priceDropTime = moment.tz(level.date_start, this.tz.auctionTimeZone());
            let nextPriceDropTime = this.getNextPriceDropTime(level);
            let dates = [nextPriceDropTime];
            let campaign = this.getCampaignByTime(nextPriceDropTime);
            if (campaign && campaign.date_end) {
                dates.push(moment.tz(campaign.date_start, this.tz.auctionTimeZone()));
                dates.push(moment.tz(campaign.date_ended, this.tz.auctionTimeZone()));
                dates.push(moment.tz(campaign.date_end, this.tz.auctionTimeZone()));
                return moment.min(dates.filter(d => d._isValid));
            }
            return nextPriceDropTime;
        },
        getCampaignByTime(priceDropTime) {
            for (let i = 0; i < this.auction.campaigns.length; i++) {
                let start = moment.tz(this.auction.campaigns[i].date_start, this.tz.auctionTimeZone());
                let end = moment.tz(this.auction.campaigns[i].date_end, this.tz.auctionTimeZone());
                if (priceDropTime < end && priceDropTime > start) {
                    return this.auction.campaigns[i];
                }
            }
            return null;
        },
        getPreviousPriceDropTime(level) {
            // if -1 it is a new level and has to be searched by the sequence
            // as it has been inserted before another level, it has the same sequence as its next level
            // lvlIndex = this.auction.levels.filter(lvl => lvl.sequence === level.sequence).map(l => l.sequence));
            let prevPdLvl = this.auction.levels.find((lvl) => lvl.phase == level.phase - 1 && lvl.price_drop === 1);
            if (!prevPdLvl) { // if there is no previous price drop level, it is the auction start time
                return moment.tz(this.auction.date_start, this.tz.auctionTimeZone());
            }

            let dates = [];
            dates.push(moment.tz(prevPdLvl.date_start,  this.tz.auctionTimeZone()));
            dates.push(moment.tz(prevPdLvl.date_started,  this.tz.auctionTimeZone()));
            dates.push(moment.tz(prevPdLvl.date_end,  this.tz.auctionTimeZone()));
            dates.push(moment.tz(prevPdLvl.date_ended,  this.tz.auctionTimeZone()));
            return moment.max(dates.filter(d => d._isValid));
        },
        getNextPriceDropTime(level) {
            let lvlIndex = this.auction.levels.findIndex(lvl => lvl.id === level.id);
            if (lvlIndex < 0) {
                lvlIndex = this.auction.levels.findIndex(lvl => lvl.sequence === level.sequence) - 1;
            }
            if (lvlIndex >= this.auction.levels.length - 1 || lvlIndex < 0 ) {
                return moment.tz(this.auction.date_end, this.tz.auctionTimeZone());
            }
            // get level after the given one
            let nextLevel = this.auction.levels[lvlIndex + 1];
            let dates = [];
            if (nextLevel.price_drop === 1) { // only price drops have date_start
                dates.push(moment.tz(nextLevel.date_start, this.tz.auctionTimeZone()));
                dates.push(moment.tz(nextLevel.date_started, this.tz.auctionTimeZone()));
                dates.push(moment.tz(nextLevel.date_ended, this.tz.auctionTimeZone()));
                return moment.min(dates.filter(d => d._isValid));
            }
            return this.getNextPriceDropTime(nextLevel);
        },
        getPriceDropTime(level) {
            if (level) {
                return moment.tz(level.date_start, this.tz.auctionTimeZone());
            }
            return moment.tz('00:00', this.tz.auctionTimeZone());
        },
        isThereALiveShow(level) {
            if (level) {
                let priceDropTime = moment.tz(level.date_start, this.tz.auctionTimeZone());
                let campaign = this.getCampaignByTime(priceDropTime);
                return !!(campaign && campaign.date_start);
            }
            return false;
        },
        getShowStartTime(level) {
            if (level) {
                let priceDropTime = moment.tz(level.date_start, this.tz.auctionTimeZone());
                let campaign = this.getCampaignByTime(priceDropTime);
                return campaign && campaign.date_start
                    ? moment.tz(campaign.date_start, this.tz.auctionTimeZone())
                    : this.getMinTimeForAdjustedLevel(level);
            }
            return moment.tz('00:00', this.tz.auctionTimeZone());
        },
        getShowEndTime(level) {
            if (level) {
                let priceDropTime = moment.tz(level.date_start, this.tz.auctionTimeZone());
                let campaign = this.getCampaignByTime(priceDropTime);
                return campaign && campaign.date_end
                    ? moment.tz(campaign.date_end, this.tz.auctionTimeZone())
                    : this.getMaxTimeForAdjustedLevel(level);
            }
            return moment.tz(this.auction.date_end, this.$t('locale.formatTimeTwelveHours'));
        },
        toTimeString(value) {
            return value.format(this.$t('locale.formatTimeTwelveHours'));
        },
        auctionModified() {
            this.reloadData( ['auction']);
        },
    },
    mounted() {
        this.startTimers();
    },
    beforeDestroy() {
        this.clearTimers();
    },
    created: function() {
        window.addEventListener('focus',  this.reloadData);
    },
    destroyed: function() {
        window.removeEventListener('focus', this.reloadData);
    }
}
</script>
